// Padel USA custom — wholesale catalog grid behavior.
// Powers <wholesale-order-list>: per-card quantity steppers that respect native B2B quantity rules
// (min / increment / max), a variant picker that updates price + MSRP + rules per card, a per-card
// "Add to order" that posts a single line to /cart/add.js, a live sticky cart bar fed by /cart.js,
// and client-side brand / category / search filtering over the rendered cards.
// Feature: custom-code-padelusa/features/wholesale-b2b.md

class WholesaleOrderList extends HTMLElement {
  connectedCallback() {
    this.cartUrl = this.dataset.cartUrl || '/cart';

    this.cards = Array.from(this.querySelectorAll('[data-wol-card]'));
    this.cards.forEach((card) => this.wireCard(card));

    this.cartbar = this.querySelector('[data-wol-cartbar]');
    this.noResultsEl = this.querySelector('[data-wol-noresults]');
    this.countEl = this.querySelector('[data-wol-count]');

    this.wireFilters();
    this.sortSoldOut();
    this.refreshCart();

    this.loader = this.querySelector('[data-wol-loader]');
    this.catalogLoading = !!this.loader;
    this.applyFilters();
    if (this.loader) this.autoLoad();
  }

  /* ---- per-card wiring ---------------------------------------------------- */

  wireCard(card) {
    const stepper = card.querySelector('.wol__stepper');
    const input = card.querySelector('.wol__qty');
    if (!stepper || !input) return; // sold-out card

    const select = card.querySelector('[data-wol-variant]');
    if (select) {
      stepper.dataset.variantId = select.value;
      this.syncVariant(card, select, input, stepper);
      select.addEventListener('change', () => this.syncVariant(card, select, input, stepper));
    }

    card.querySelectorAll('[data-step-down]').forEach((btn) =>
      btn.addEventListener('click', () => this.step(input, -1))
    );
    card.querySelectorAll('[data-step-up]').forEach((btn) =>
      btn.addEventListener('click', () => this.step(input, 1))
    );
    input.addEventListener('change', () => {
      input.value = this.normalize(input);
    });

    const addBtn = card.querySelector('[data-wol-add-card]');
    if (addBtn) addBtn.addEventListener('click', () => this.addCard(stepper, input, addBtn));
  }

  syncVariant(card, select, input, stepper) {
    const opt = select.options[select.selectedIndex];
    if (!opt) return;
    stepper.dataset.variantId = opt.value;

    const min = parseInt(opt.dataset.min, 10) || 1;
    const step = parseInt(opt.dataset.step, 10) || 1;
    input.dataset.min = min;
    input.dataset.step = step;
    input.step = step;
    if (opt.dataset.max) {
      input.dataset.max = opt.dataset.max;
    } else {
      delete input.dataset.max;
    }

    const priceEl = card.querySelector('[data-price-display]');
    if (priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;

    // MSRP lives in a static "MSRP <s>…</s>" span — update the <s> child's text only (no innerHTML).
    const msrpEl = card.querySelector('[data-msrp]');
    if (msrpEl) {
      if (opt.dataset.msrp) {
        const struck = msrpEl.querySelector('s');
        if (struck) struck.textContent = opt.dataset.msrp;
        msrpEl.hidden = false;
      } else {
        msrpEl.hidden = true;
      }
    }

    const offEl = card.querySelector('[data-off]');
    if (offEl) {
      if (opt.dataset.off) {
        offEl.textContent = opt.dataset.off + '% off MSRP';
        offEl.hidden = false;
      } else {
        offEl.hidden = true;
      }
    }

    const minEl = card.querySelector('[data-min-display]');
    if (minEl) {
      if (min > 1) {
        minEl.textContent = `Min ${min}`;
        minEl.hidden = false;
      } else {
        minEl.hidden = true;
      }
    }

    const skuEl = card.querySelector('[data-sku-display]');
    if (skuEl) {
      if (opt.dataset.sku) {
        skuEl.textContent = 'SKU ' + opt.dataset.sku;
        skuEl.hidden = false;
      } else {
        skuEl.hidden = true;
      }
    }

    // Keep the entered quantity valid against the new variant's rules.
    input.value = this.normalize(input);
  }

  /* ---- quantity rules ----------------------------------------------------- */

  rules(input) {
    return {
      min: parseInt(input.dataset.min, 10) || 1,
      step: parseInt(input.dataset.step, 10) || 1,
      max: input.dataset.max ? parseInt(input.dataset.max, 10) : null,
    };
  }

  step(input, dir) {
    const { min, step, max } = this.rules(input);
    let q = parseInt(input.value, 10) || 0;
    if (dir > 0) {
      q = q < min ? min : q + step;
    } else {
      q = q <= min ? 0 : q - step;
    }
    if (max && q > max) q = max;
    input.value = q < 0 ? 0 : q;
    input.value = this.normalize(input);
  }

  // Clamp a typed/stepped value to the variant's min / increment / max. 0 means "not ordered".
  normalize(input) {
    const { min, step, max } = this.rules(input);
    let q = parseInt(input.value, 10) || 0;
    if (q <= 0) return 0;
    if (q < min) q = min;
    if (step > 1) {
      const rem = (q - min) % step;
      if (rem !== 0) q += step - rem;
    }
    if (max && q > max) {
      q = max - ((max - min) % step);
    }
    return q;
  }

  /* ---- add to cart -------------------------------------------------------- */

  async addCard(stepper, input, addBtn) {
    const id = stepper.dataset.variantId;
    const qty = this.normalize(input);
    if (!id || qty < 1) {
      input.value = this.rules(input).min;
      return;
    }

    const original = addBtn.textContent;
    addBtn.setAttribute('aria-disabled', 'true');
    addBtn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add');
      }
      addBtn.textContent = 'Added ✓';
      await this.refreshCart();
      window.setTimeout(() => {
        addBtn.textContent = original;
        addBtn.removeAttribute('aria-disabled');
      }, 1200);
    } catch (e) {
      addBtn.textContent = e.message || 'Try again';
      window.setTimeout(() => {
        addBtn.textContent = original;
        addBtn.removeAttribute('aria-disabled');
      }, 1800);
    }
  }

  async refreshCart() {
    if (!this.cartbar) return;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const skus = Array.isArray(cart.items) ? cart.items.length : 0;
      const items = cart.item_count || 0;

      this.setText('[data-wol-skus]', `${skus} ${skus === 1 ? 'SKU' : 'SKUs'}`);
      this.setText('[data-wol-items]', `${items} ${items === 1 ? 'item' : 'items'}`);
      this.setText('[data-wol-total]', this.money(cart.total_price || 0, cart.currency));
      this.cartbar.hidden = items === 0;
    } catch (e) {
      /* leave the bar as-is on a transient error */
    }
  }

  setText(selector, text) {
    const el = this.cartbar.querySelector(selector);
    if (el) el.textContent = text;
  }

  money(cents, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ---- sold-out ordering -------------------------------------------------- */

  // Push sold-out cards to the end of the grid — they can't be ordered anyway.
  sortSoldOut() {
    const grid = this.querySelector('[data-wol-grid]');
    if (!grid) return;
    this.cards
      .filter((card) => card.classList.contains('wol__card--soldout'))
      .forEach((card) => grid.appendChild(card));
  }

  /* ---- progressive catalog load ------------------------------------------- */

  // Fetch every remaining page in parallel and append into one grid, so filters span the
  // whole catalog quickly and there's no page reset. Degrades gracefully if gated (logged-out
  // / theme editor): those fetches return no cards, so nothing is appended.
  async autoLoad() {
    const statusEl = this.loader.querySelector('[data-wol-loader-status]');
    const btn = this.loader.querySelector('[data-wol-loader-btn]');
    const grid = this.querySelector('[data-wol-grid]');
    const base = this.loader.dataset.baseUrl || window.location.pathname;
    const total = parseInt(this.loader.dataset.totalPages, 10) || 1;
    const current = parseInt(this.loader.dataset.currentPage, 10) || 1;

    const pages = [];
    for (let p = current + 1; p <= total; p += 1) pages.push(p);
    if (!pages.length) {
      this.finishLoad();
      return;
    }

    try {
      const results = await Promise.all(
        pages.map((p) =>
          fetch(`${base}?page=${p}`, { headers: { Accept: 'text/html' } }).then((r) => {
            if (!r.ok) throw new Error('fetch failed');
            return r.text().then((html) => ({ p, html }));
          })
        )
      );
      results.sort((a, b) => a.p - b.p);
      results.forEach(({ html }) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-wol-grid] [data-wol-card]').forEach((card) => {
          const node = document.importNode(card, true);
          grid.appendChild(node);
          this.cards.push(node);
          this.wireCard(node);
        });
      });
      this.finishLoad();
    } catch (e) {
      this.catalogLoading = false;
      if (statusEl) statusEl.hidden = true;
      if (btn) {
        btn.hidden = false;
        btn.onclick = () => {
          btn.hidden = true;
          if (statusEl) statusEl.hidden = false;
          this.autoLoad();
        };
      }
    }
  }

  finishLoad() {
    this.catalogLoading = false;
    this.applyFilters();
    this.sortSoldOut();
    if (this.loader) this.loader.remove();
  }

  /* ---- filtering ---------------------------------------------------------- */

  wireFilters() {
    this.filterRoot = this.querySelector('[data-wol-filters]');
    this.activeVendor = 'all';
    this.activeType = 'all';
    this.search = '';
    if (!this.filterRoot) return;

    this.filterRoot.querySelectorAll('[data-filter-vendor]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.activeVendor = btn.dataset.filterVendor;
        this.markActive(btn, '[data-filter-vendor]');
        this.applyFilters();
      })
    );
    this.filterRoot.querySelectorAll('[data-filter-type]').forEach((btn) =>
      btn.addEventListener('click', () => {
        this.activeType = btn.dataset.filterType;
        this.markActive(btn, '[data-filter-type]');
        this.applyFilters();
      })
    );

    const search = this.filterRoot.querySelector('[data-wol-search]');
    if (search) {
      search.addEventListener('input', () => {
        this.search = search.value.trim().toLowerCase();
        this.applyFilters();
      });
    }
  }

  markActive(activeBtn, groupSelector) {
    this.filterRoot.querySelectorAll(groupSelector).forEach((btn) => {
      const on = btn === activeBtn;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  applyFilters() {
    let shown = 0;
    this.cards.forEach((card) => {
      const okVendor = this.activeVendor === 'all' || card.dataset.vendor === this.activeVendor;
      const okType = this.activeType === 'all' || card.dataset.type === this.activeType;
      const okSearch =
        !this.search ||
        (card.dataset.title || '').includes(this.search) ||
        (card.dataset.vendor || '').toLowerCase().includes(this.search) ||
        (card.dataset.tags || '').includes(this.search);
      const show = okVendor && okType && okSearch;
      card.hidden = !show;
      if (show) shown += 1;
    });
    if (this.countEl) {
      this.countEl.textContent = `${shown} ${shown === 1 ? 'product' : 'products'}`;
    }
    if (!this.noResultsEl) return;
    if (shown > 0) {
      this.noResultsEl.hidden = true;
    } else {
      this.noResultsEl.textContent = this.catalogLoading
        ? 'Loading the full catalog — this filter will fill in once it finishes.'
        : 'No products match these filters.';
      this.noResultsEl.hidden = false;
    }
  }
}

if (!customElements.get('wholesale-order-list')) {
  customElements.define('wholesale-order-list', WholesaleOrderList);
}
